import React, { useEffect, useRef } from 'react';
import Drawer from '@mui/material/Drawer';
import useIsMobile from './useIsMobile';

// Header height (h-[55px] in Header.tsx) and right rail width (.rail { width: 56px })
const HEADER_H = 55;
const RAIL_W = 56;
// Высота нижней мобильной таб-панели (.m-tabbar) — панель не должна её перекрывать
const TABBAR_H = 64;

// Module-level: tracks the currently open panel instance
let activePanel: { close: () => void } | null = null;

interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  width?: number | string;
  keepMounted?: boolean;
  paperStyle?: React.CSSProperties;
  children: React.ReactNode;
  /** Не закрывать при клике вне панели — только через кнопки внутри */
  persistent?: boolean;
}

const SidePanel: React.FC<SidePanelProps> = ({
  open, onClose, width = 420, keepMounted, paperStyle, children, persistent,
}) => {
  const isMobile = useIsMobile();
  // На мобилке нет правого рельса — панель во всю ширину экрана без отступа
  const railOffset = isMobile ? 0 : RAIL_W;
  const panelWidth = isMobile ? '100vw' : width;
  // На мобилке панель заканчивается над нижним таб-баром, чтобы он оставался видимым
  const mobileBottom = isMobile ? `calc(${TABBAR_H}px + env(safe-area-inset-bottom))` : 0;

  // Stable ref per instance — identity used to distinguish panels
  const instanceRef = useRef<{ close: () => void }>({ close: onClose });
  instanceRef.current.close = onClose;
  const paperRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      if (activePanel && activePanel !== instanceRef.current) {
        activePanel.close();
      }
      activePanel = instanceRef.current;
    } else {
      if (activePanel === instanceRef.current) {
        activePanel = null;
      }
    }
  }, [open]);

  // Close on click outside the panel (header, right rail, content area).
  //
  // ПРАВИЛО ДЛЯ МОДАЛЬНЫХ ОКОН ПОВЕРХ ПАНЕЛИ:
  // Любое всплывающее окно (modal, dropdown), которое рендерится через React-портал
  // в document.body, должно добавлять атрибут data-portal-modal="true" на свой корневой div.
  // Это позволяет SidePanel игнорировать клики внутри таких окон и не закрываться.
  // Z-index таких окон должен быть выше MUI Drawer (1200) — использовать var(--z-portal, 1600).
  useEffect(() => {
    if (!open || persistent) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // Клик по элементу, который к моменту обработки уже удалён из DOM
      // (например, крестик "очистить" в react-select — он исчезает при клике,
      // и его нельзя считать кликом снаружи панели).
      if (!document.contains(target)) return;
      // Не закрывать панель если клик внутри портал-модала (окно поверх панели)
      if ((target as Element)?.closest?.('[data-portal-modal], .app-rs__menu')) return;
      if (paperRef.current && !paperRef.current.contains(target)) {
        onClose();
      }
    };
    // setTimeout(0) prevents catching the same click that opened the panel
    const id = setTimeout(() => document.addEventListener('mousedown', handleMouseDown), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [open, onClose, persistent]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={persistent ? () => {} : (_, reason) => {
        if (reason === 'backdropClick' && document.querySelector('[data-portal-modal]')) return;
        onClose();
      }}
      ModalProps={{
        ...(keepMounted !== undefined ? { keepMounted } : {}),
        // На мобилке корень модалки не заходит на таб-бар, иначе он бы перехватывал тапы
        ...(isMobile ? { style: { bottom: mobileBottom } as React.CSSProperties } : {}),
        componentsProps: {
          backdrop: {
            style: {
              top: HEADER_H,
              right: railOffset,
              bottom: mobileBottom,
              background: 'transparent',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
            },
          },
        },
      }}
      PaperProps={{
        ref: (el: any) => { paperRef.current = el; },
        className: 'side-panel-paper',
        style: {
          top: HEADER_H,
          right: railOffset,
          height: isMobile
            ? `calc(100% - ${HEADER_H}px - ${TABBAR_H}px - env(safe-area-inset-bottom))`
            : `calc(100% - ${HEADER_H}px)`,
          width: panelWidth,
          maxWidth: '100vw',
          overflow: 'hidden',
          ...paperStyle,
        },
      }}
    >
      {children}
    </Drawer>
  );
};

export default SidePanel;
